/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { confirmAlert } from 'react-confirm-alert'; // Import

/**
 * Internal dependencies
 */
import { getCurrentKiosk, getCurrentKioskId } from 'state/current-kiosk/selectors';
import { removeScheduledChanges } from 'state/subscriptions/actions';
import cscom from 'lib/customer-site-api';
import Loader from './loader';
import { successNotice } from 'state/notices/actions';
import Money from 'components/money';
import PlanName from 'components/plan-name';
import DateTime from 'components/date-time';
import Button from 'components/button';
import ConfirmCancelScheduledChangesDialog from './confirm-cancel-scheduled-changes-dialog';
import DetailListItem from 'components/detail-list-item';
import { Box, Stack, Text, Heading, Divider } from '@towersystems/towersystems-design-system';
import Card from 'components/card';

const ScheduledChangesCard = (props) => {
	const [scheduledChanges, setScheduledChanges] = useState(null);
	const [requesting, setRequesting] = useState(null);

	useEffect(() => {
		if (props.subscriptionId) {
			setRequesting(true);
			cscom
				.subscription()
				.getScheduledChanges(props.subscriptionId)
				.then((scheduledChanges) => setScheduledChanges(scheduledChanges))
				.finally(() => setRequesting(false));
		}
	}, [props.subscriptionId, get(props, ['subscription', 'updated_at'])]);

	const removeScheduledChanges = () => {
		setRequesting(true);
		confirmAlert({
			title: 'Remove Scheduled Changes',
			message: 'Are you sure to do this.',
			closeOnEscape: false,
			closeOnClickOutside: false,
			customUI: ({ onClose }) => {
				return (
					<ConfirmCancelScheduledChangesDialog
						onClose={onClose}
						onSubmit={() => {
							return props
								.removeScheduledChanges(props.subscriptionId)
								.then(() => setScheduledChanges(null))
								.then(() =>
									props.successNotice(
										'Your pending changes to your web subscription have been cancelled.',
									),
								);
						}}
					/>
				);
			},
		});
	};

	if (!scheduledChanges && requesting) {
		return <Loader />;
	}

	if (!scheduledChanges) {
		return null;
	}

    return (
        <Card>
            <Card.Header>
                <Box display='flex' justifyContent='spaceBetween' alignItems='center'>
                    <Heading level='3'>Scheduled Changes</Heading>
                    <Button compact onClick={removeScheduledChanges}>
                        Cancel
                    </Button>
                </Box>
            </Card.Header>
            <Stack dividers>
                <Card.Section>
                    <DetailListItem
                        label='Plan Price'
                        detail={
                            <Text size='small'>
                                <Money value={scheduledChanges.plan_unit_price / 100} /> /{' '}
                                {scheduledChanges.billing_period} {scheduledChanges.billing_period_unit}
                            </Text>
                        }
                    />
                </Card.Section>
                {scheduledChanges.addons && (
                    <Card.Section>
                        <DetailListItem
                            label='Additional Stores'
                            detail={
                                <Text>
                                    <Money
                                        value={
                                            (scheduledChanges.addons[0].unit_price *
                                                scheduledChanges.addons[0].quantity) /
                                            100
                                        }
                                    />
                                    <span> (x{scheduledChanges.addons[0].quantity})</span>
                                </Text>
                            }
                        />
                    </Card.Section>
                )}

                <Card.Section>
                    <DetailListItem
                        label='Plan Activation Date'
                        detail={<DateTime date={scheduledChanges.next_billing_at} format='Do MMM YYYY' />}
                    />
                </Card.Section>
            </Stack>
        </Card>
    );

};

export default connect(
	(state) => ({
		subscription: getCurrentKiosk(state),
		subscriptionId: getCurrentKioskId(state),
	}),
	{
		removeScheduledChanges,
		successNotice,
	},
)(ScheduledChangesCard);
